import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { ErrorMessage } from '@hookform/error-message';
import { cn } from '@workspace/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { useEffect, useState } from 'react';
import type { FieldValues, Path, RegisterOptions } from 'react-hook-form';
import { Controller, useFormContext } from 'react-hook-form';
import { RenderedContent } from '../../utils';
import { Field, Label, LabelProps, TextError } from '../form';

const comboboxInputVariants = cva(
  [
    'w-full rounded-md border px-3 py-2 pr-10 text-sm transition-all duration-200',
    'focus:outline-none focus:ring-2 focus:ring-offset-0',
    'placeholder:text-input-text-placeholder',
    'disabled:cursor-not-allowed',
  ],
  {
    variants: {
      variant: {
        default: [
          'bg-input-bg border-input-border text-input-text',
          'focus:border-input-border-focus focus:ring-primary/20',
          'disabled:bg-input-bg-disabled disabled:text-input-text-disabled disabled:border-input-border-disabled',
        ],
        error: [
          'bg-input-bg-error border-input-border-error text-input-text',
          'focus:border-input-border-error focus:ring-error/20',
          'disabled:bg-input-bg-disabled disabled:text-input-text-disabled disabled:border-input-border-disabled',
        ],
        success: [
          'bg-input-bg-success border-input-border-success text-input-text',
          'focus:border-input-border-success focus:ring-success/20',
          'disabled:bg-input-bg-disabled disabled:text-input-text-disabled disabled:border-input-border-disabled',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

const comboboxOptionsVariants = cva([
  'absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md py-1 text-sm',
  'bg-dropdown-bg border border-dropdown-border',
  'shadow-dropdown',
  'focus:outline-none',
]);

const comboboxOptionVariants = cva(['relative cursor-pointer select-none py-2 px-4 pr-10 transition-colors'], {
  variants: {
    state: {
      default: 'text-foreground hover:bg-dropdown-item-hover-bg',
      active: 'bg-dropdown-item-active-bg text-dropdown-item-active-text',
    },
  },
  defaultVariants: {
    state: 'default',
  },
});

const loaderVariants = cva('rounded-full border-2 border-r-transparent animate-spin', {
  variants: {
    size: {
      sm: 'h-3 w-3',
      default: 'h-4 w-4',
    },
    variant: {
      default: 'border-primary border-t-transparent',
      error: 'border-error border-t-transparent',
    },
  },
  defaultVariants: {
    size: 'default',
    variant: 'default',
  },
});

export interface ComboboxOption<T = any> {
  value: string;
  label: string;
  data?: T;
}

export interface FieldComboboxProps<TFormValues extends Record<string, any> = any, TOption = any>
  extends LabelProps,
    VariantProps<typeof comboboxInputVariants> {
  label: RenderedContent;
  name: Path<TFormValues> | string;
  options?: ComboboxOption<TOption>[];
  validation?: RegisterOptions<FieldValues, string> | undefined;
  disabled?: boolean;
  placeholder?: string;
  loading?: boolean;
  onInputChange?: (value: string) => void;
  onChange?: (option: ComboboxOption<TOption> | null) => void;
  filterOptions?: boolean;
  emptyMessage?: string;
  loadingMessage?: string;
  minQueryLength?: number;
  renderOption?: (option: ComboboxOption<TOption>, selected: boolean) => React.ReactNode;
}

export function FieldCombobox<TFormValues extends Record<string, any> = any, TOption = any>({
  label,
  name,
  options = [],
  required = true,
  validation,
  variant,
  disabled,
  placeholder = 'Sélectionnez ou recherchez',
  loading = false,
  onInputChange,
  onChange,
  filterOptions = true,
  emptyMessage = 'Aucun résultat trouvé.',
  loadingMessage = 'Recherche en cours...',
  minQueryLength = 0,
  renderOption,
}: FieldComboboxProps<TFormValues, TOption>) {
  const methods = useFormContext();
  const [query, setQuery] = useState('');

  // Filtrage (optionnel selon props)
  const filteredOptions = filterOptions ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase())) : options;

  // Gestion de la query
  const handleInputChange = (value: string) => {
    setQuery(value);
    onInputChange?.(value);
  };

  disabled = disabled || methods.formState.isSubmitting;

  return (
    <Field>
      <Label required={required}>{label}</Label>
      <Controller
        name={name}
        control={methods.control}
        rules={{
          required: required ? `${label} est requis` : false,
          ...validation,
        }}
        render={({ field, fieldState }) => {
          // Synchroniser query avec la valeur du champ sélectionné
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useEffect(() => {
            const selectedOption = options.find((opt) => opt.value === field.value);
            if (selectedOption && query !== selectedOption.label) {
              setQuery(selectedOption.label);
            } else if (!selectedOption && field.value && query !== field.value) {
              setQuery(field.value);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
          }, [field.value, options, query]);

          // Déterminer la variante basée sur l'état du champ
          const computedVariant = fieldState.error ? 'error' : variant || 'default';

          // Trouver option sélectionnée
          let selectedOption = options.find((opt) => opt.value === field.value);
          if (!selectedOption && field.value) {
            selectedOption = { value: field.value, label: field.value } as ComboboxOption;
          }

          return (
            <Combobox
              value={selectedOption || null}
              onChange={(option: ComboboxOption<TOption> | null) => {
                const newValue = option?.value || '';
                field.onChange(newValue);
                onChange?.(option);

                // Synchroniser la query avec la sélection
                if (option) {
                  setQuery(option.label);
                } else {
                  setQuery('');
                }
              }}
              disabled={disabled}
            >
              <div className="relative">
                <div className="relative">
                  <ComboboxInput
                    className={comboboxInputVariants({ variant: computedVariant })}
                    displayValue={(option: ComboboxOption<TOption> | null) => option?.label || ''}
                    onChange={(e) => handleInputChange(e.target.value)}
                    placeholder={placeholder}
                    autoComplete="off"
                  />

                  <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
                    <ChevronUpDownIcon
                      className="h-5 w-5 text-muted-foreground"
                      aria-hidden="true"
                    />
                  </ComboboxButton>

                  {loading && (
                    <div className="absolute right-10 top-1/2 -translate-y-1/2">
                      <div className={loaderVariants({ variant: fieldState.error ? 'error' : 'default' })} />
                    </div>
                  )}
                </div>

                {query.trim().length >= minQueryLength && (
                  <ComboboxOptions className={comboboxOptionsVariants()}>
                    {filteredOptions.length === 0 && !loading ? (
                      <div className="relative cursor-default select-none px-4 py-2 text-muted-foreground">{emptyMessage}</div>
                    ) : loading && filteredOptions.length === 0 ? (
                      <div className="relative cursor-default select-none px-4 py-2 text-muted-foreground flex items-center gap-2">
                        <div className={loaderVariants({ size: 'sm', variant: 'default' })} />
                        {loadingMessage}
                      </div>
                    ) : (
                      filteredOptions.map((option) => (
                        <ComboboxOption
                          key={option.value}
                          value={option}
                          className={({ active }) => comboboxOptionVariants({ state: active ? 'active' : 'default' })}
                        >
                          {({ selected }) => (
                            <>
                              {renderOption ? (
                                renderOption(option, selected)
                              ) : (
                                <span className={cn('block truncate', selected && 'font-medium')}>{option.label}</span>
                              )}
                              {selected && (
                                <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary">
                                  <CheckIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                  />
                                </span>
                              )}
                            </>
                          )}
                        </ComboboxOption>
                      ))
                    )}
                  </ComboboxOptions>
                )}

                {minQueryLength > 0 && query.trim().length > 0 && query.trim().length < minQueryLength && (
                  <div className="absolute z-20 mt-1 w-full rounded-md bg-dropdown-bg border border-dropdown-border py-2 px-3 text-xs text-muted-foreground shadow-dropdown">
                    Tapez au moins {minQueryLength} caractères pour rechercher
                  </div>
                )}
              </div>
            </Combobox>
          );
        }}
      />
      <ErrorMessage
        errors={methods.formState.errors}
        name={name}
        render={({ message }) => <TextError>{message}</TextError>}
      />
    </Field>
  );
}
